import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const DOMAIN_LABELS = {
  D1: 'Opening and Rapport Building',
  D2: 'Concern / Belief Elicitation',
  D3: 'Empathy and Emotional Responsiveness',
  D4: 'Clarity of Explanation',
  D5: 'Collaborative Planning / Shared Decision-Making',
  D6: 'Avoidance of Stigmatising Language',
  D7: 'Teach-Back / Checking Understanding / Safety-Netting'
};

const scoreClass = (score) => {
  if (score == null) return 'bg-gray-100 text-gray-700';
  if (score >= 4) return 'bg-green-100 text-green-800';
  if (score === 3) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

const DiscourseAnalysisReport = ({
  transcript,
  patientId,
  doctorId,
  durationSeconds,
  clinicalContext,
  empathyScore,
  consultationSessionId,
  caseContext,
  language
}) => {
  const location = useLocation();
  const selectedPatient = location.state?.patient;

  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedSessionId, setResolvedSessionId] = useState(
    consultationSessionId || null
  );

  const resolvedLanguage = language || 'English';

  const resolvedCaseContext = useMemo(() => {
    if (caseContext && Object.keys(caseContext).length > 0) return caseContext;

    return {
      title: selectedPatient?.['Case Type'] || '',
      description: selectedPatient?.['Clinical Data'] || '',
      patient_statement:
        selectedPatient?.['Patient Quote (English)'] ||
        selectedPatient?.['Patient Quote (India-centric)'] ||
        '',
      objective: selectedPatient?.['Consult Prompt'] || ''
    };
  }, [caseContext, selectedPatient]);

  useEffect(() => {
    if (consultationSessionId) {
      setResolvedSessionId(consultationSessionId);
      return;
    }

    const handleSavedSession = (event) => {
      const sessionId = event?.detail?.sessionId;
      if (sessionId) setResolvedSessionId(String(sessionId));
    };

    window.addEventListener('consultation-session-saved', handleSavedSession);
    return () => window.removeEventListener('consultation-session-saved', handleSavedSession);
  }, [consultationSessionId]);

  useEffect(() => {
    if (transcript && transcript.length > 10 && resolvedSessionId) {
      fetchAnalysis();
    }
  }, [transcript, resolvedSessionId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setError(null);

    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_PROD_API_BASE_URL || 'https://consult.conversationaily.ai')
        : (process.env.REACT_APP_DEV_API_BASE_URL || 'https://testone.conversationaily.ai');

      const response = await axios.post(`${API_BASE_URL}/api/discourse/analyze`, {
        consultation_session_id: resolvedSessionId,
        transcript,
        patient_id: patientId,
        doctor_id: doctorId,
        duration_seconds: durationSeconds,
        clinical_context: clinicalContext,
        empathy_score: empathyScore,
        language: resolvedLanguage,
        case_context: resolvedCaseContext
      });

      setAnalysis(response.data?.report || response.data);
    } catch (err) {
      console.error('Discourse analysis failed:', err);
      setAnalysis(null);
      setError(err.response?.data?.message || 'Unable to generate discourse analysis.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-5xl mx-auto border">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Communication Competency Report</h3>
        <span className="text-gray-700">Analyzing the consultation against the pilot competency framework...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-5xl mx-auto border">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Communication Competency Report</h3>
        <div className="text-red-700">{error}</div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-5xl mx-auto border">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">Communication Competency Report</h3>
        <div className="text-gray-600">The competency report will appear after the consultation session has been saved.</div>
      </div>
    );
  }

  const technicalFlags = analysis.technical_quality?.flags || [];
  const domains = analysis.domains || {};
  const analyzable = analysis.analyzability === 'Yes';

  return (
    <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-6 max-w-5xl mx-auto border text-gray-800">
      <div className="border-b border-gray-300 pb-4 mb-6 text-center">
        <h2 className="text-xl font-bold text-blue-800">ConversationAIly™</h2>
        <h3 className="text-lg font-semibold text-gray-800">One-Way Clinical Communication Competency Report</h3>
        <p className="text-xs text-gray-500 mt-1">Aligned to the IMS ConversationAIly pilot faculty rater domains</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5 text-sm">
        <div><strong>Doctor ID:</strong> {analysis.doctor_id || doctorId}</div>
        <div><strong>Consult ID:</strong> {analysis.consult_id || patientId}</div>
        <div><strong>Clinical Context:</strong> {analysis.clinical_context || clinicalContext}</div>
        <div><strong>Language:</strong> {analysis.language || resolvedLanguage}</div>
        <div><strong>Duration:</strong> {analysis.duration || `${durationSeconds}s`}</div>
        <div><strong>Communication Mode:</strong> {analysis.communication_mode || 'N/A'}</div>
      </div>

      <div className="mb-5 rounded border bg-white p-4">
        <div className="flex flex-wrap gap-6 items-center">
          <div>
            <span className="text-sm font-semibold">Analyzability:</span>{' '}
            <span className={`px-2 py-1 rounded text-sm ${analyzable ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
              {analysis.analyzability || 'Not assessed'}
            </span>
          </div>
          {analyzable && (
            <>
              <div><span className="text-sm font-semibold">Domain Average:</span> {analysis.domain_average ?? 'N/A'} / 5</div>
              <div>
                <span className="text-sm font-semibold">Holistic Communication Score:</span>{' '}
                <span className={`px-2 py-1 rounded ${analysis.holistic_score >= 7 ? 'bg-green-100 text-green-800' : analysis.holistic_score >= 4 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                  {analysis.holistic_score ?? 'N/A'} / 9 · {analysis.holistic_band || ''}
                </span>
              </div>
            </>
          )}
        </div>
        {!analyzable && analysis.exclusion_reason && (
          <p className="text-sm mt-3 text-orange-800"><strong>Reason:</strong> {analysis.exclusion_reason}</p>
        )}
      </div>

      {technicalFlags.length > 0 && (
        <div className="mb-5 rounded border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800">
          <strong>Technical quality:</strong> {technicalFlags.join(' · ')}
        </div>
      )}

      {analyzable && (
        <>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse border border-gray-300 text-sm bg-white">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left">Domain</th>
                  <th className="border border-gray-300 p-3 text-center w-20">Score</th>
                  <th className="border border-gray-300 p-3 text-left">Evidence from Transcript</th>
                  <th className="border border-gray-300 p-3 text-left">Competency Interpretation</th>
                  <th className="border border-gray-300 p-3 text-left">Practical Next Step</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(DOMAIN_LABELS).map(([key, label]) => {
                  const domain = domains[key] || {};
                  return (
                    <tr key={key} className="align-top">
                      <td className="border border-gray-300 p-3">
                        <strong>{key}</strong><br />{label}
                      </td>
                      <td className="border border-gray-300 p-3 text-center">
                        <span className={`inline-block px-2 py-1 rounded font-semibold ${scoreClass(domain.score)}`}>
                          {domain.score ?? '—'} / 5
                        </span>
                        <div className="text-xs mt-1 text-gray-500">{domain.rating || ''}</div>
                      </td>
                      <td className="border border-gray-300 p-3">{domain.evidence || 'No specific evidence identified.'}</td>
                      <td className="border border-gray-300 p-3">{domain.interpretation || '—'}</td>
                      <td className="border border-gray-300 p-3">{domain.coaching || 'No priority change identified.'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
            <div className="rounded border bg-white p-4">
              <h4 className="font-semibold mb-2">Overall Communication Judgment</h4>
              <p className="text-sm">{analysis.holistic_rationale || 'No holistic rationale returned.'}</p>
            </div>
            <div className="rounded border bg-white p-4">
              <h4 className="font-semibold mb-2">Case Objective Alignment</h4>
              <p className="text-sm"><strong>{analysis.case_objective_alignment || 'Not assessable'}</strong></p>
              {analysis.case_objective_evidence && <p className="text-sm mt-1">{analysis.case_objective_evidence}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded border border-green-200 bg-green-50 p-4">
              <h4 className="font-semibold text-green-900 mb-2">What Worked Well</h4>
              {analysis.strengths?.length > 0 ? (
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {analysis.strengths.slice(0, 2).map((item, idx) => <li key={idx}>{item}</li>)}
                </ul>
              ) : <p className="text-sm">No specific strength was identified with sufficient evidence.</p>}
            </div>

            <div className="rounded border border-blue-200 bg-blue-50 p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Priority Development Areas</h4>
              {analysis.priority_improvements?.length > 0 ? (
                <ul className="list-disc pl-5 text-sm space-y-1">
                  {analysis.priority_improvements.slice(0, 2).map((item, idx) => <li key={idx}>{item}</li>)}
                </ul>
              ) : <p className="text-sm">No high-priority communication change was identified from this sample.</p>}
            </div>
          </div>

          {(analysis.red_flags?.length > 0 || analysis.stigmatizing_phrases?.length > 0) && (
            <div className="mt-5 rounded border border-red-200 bg-red-50 p-4 text-sm">
              {analysis.red_flags?.length > 0 && <p><strong>Evidence-supported red flags:</strong> {analysis.red_flags.join(' · ')}</p>}
              {analysis.stigmatizing_phrases?.length > 0 && <p className="mt-1"><strong>Contextually stigmatizing language:</strong> {analysis.stigmatizing_phrases.join(' · ')}</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DiscourseAnalysisReport;
